import { getMessage, getUserApplicationRateUnits } from '@tcc/i18n';
import { Icon } from '@tcc/crate';
import cx from 'classnames';
import { ApplicationProduct } from '../../../types/Application';
import { SortField } from '../../../types/Sort';
import { getApplicationLocaleGroup, getApplicationAmountLocaleGroup } from '../../../utils/newProcessUnits';
import styles from './ApplicationsPanel.css';
import { createUnit } from '../../../i18n/unitQtyWrapper';

interface ProductTableProps {
  products: ApplicationProduct[];
  sortField: SortField;
  sortProducts: (products: ApplicationProduct[]) => ApplicationProduct[];
  handleSort: (field: SortField) => void;
  getSortIcon: (field: SortField) => string;
}

const ProductTable = ({
  products,
  sortField,
  sortProducts,
  handleSort,
  getSortIcon,
}: ProductTableProps) => {
  if (products.length === 0) {
    return null;
  }

  return (
    <div className={styles.applicationsPanelProductBreakdown}>
      <table className={styles.applicationsPanelProductTable} data-test-id="application-cards-product-breakdown">
        <thead>
          <tr>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.NAME)}
                aria-label="Product"
              >
                {getMessage('application.details.product')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.NAME })}
                  glyph={getSortIcon(SortField.NAME)}
                />
              </button>
            </th>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.RATE)}
                aria-label="Rate"
              >
                {getMessage('application.details.rate')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.RATE })}
                  glyph={getSortIcon(SortField.RATE)}
                />
              </button>
            </th>
            <th className={styles.applicationsPanelProductTableHeader}>
              <button 
                type="button"
                className={styles.applicationsPanelSortButton}
                onClick={() => handleSort(SortField.APPLIED)}
                aria-label="Applied"
              >
                {getMessage('application.details.applied')}
                <Icon
                  className={cx(styles.applicationsPanelSortIcon, { [styles.applicationsPanelSortActive]: sortField === SortField.APPLIED })}
                  glyph={getSortIcon(SortField.APPLIED)}
                />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortProducts(products).map((product, index) => {
            const growerInputRateUnits = getUserApplicationRateUnits(
              product.growerInputUnit
            );
            
            let applicationRate = createUnit(
              product.rate.q,
              product.rate.u,
            ).forLocaleGroup(getApplicationLocaleGroup(product.rate.u));

            if (growerInputRateUnits) {
              try {
                applicationRate = applicationRate.to(growerInputRateUnits);
              } catch (e) {
                // Do nothing: Use fallback computed units instead of user selected ones
              }
            }

            let totalApplied = createUnit(
              product.applied.q,
              product.applied.u,
            ).forLocaleGroup(getApplicationAmountLocaleGroup(product.applied.u));

            if (product.growerInputUnit) {
              try {
                totalApplied = totalApplied.to(product.growerInputUnit);
              } catch (e) {
                // Do nothing: Use fallback computed units instead of user selected ones
              }
            }
            
            return (
              <tr key={`${product.name}-${product.applied.q}`} data-test-id={`application-card-product-row-${index}`}>
                <td className={styles.applicationsPanelProductTableCell}>
                  {product.name}
                </td>
                <td className={styles.applicationsPanelProductTableCell}>
                  <span className={styles.applicationsPanelValueText}>{applicationRate.scalarLocaleString}</span>
                  <br />
                  <span className={styles.applicationsPanelUnitText}>{applicationRate.symbol.quantitySpecific}</span>
                </td>
                <td className={styles.applicationsPanelProductTableCell}>
                  <span className={styles.applicationsPanelValueText}>{totalApplied.scalarLocaleString}</span>
                  <br />
                  <span className={styles.applicationsPanelUnitText}>{totalApplied.symbol.quantitySpecific}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
export type { ProductTableProps };
